/* eslint-disable no-underscore-dangle */
// @flow
import React, { Suspense } from 'react';
import type { Dispatch } from 'redux';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { withRouter, matchPath } from 'react-router-dom';
import { OmniNav } from '@finect/tabular-omninav';
import { finect as theme } from '@finect/tabular-themes/finect';
import { debounce } from '@finect/front-utils/debounce';
import { reset } from '@finect/tabular-utils/reset';
import { isPreRelease } from '@finect/front-utils/isPreRelease';
import { Main } from '@finect/tabular-core/Main';
import { Content } from '@finect/tabular-core/Content';
import CookiesCMP from '@finect/front-core/CookiesCMP';
import { Footer } from '@finect/tabular-components/Footer';
import { getLoggedUser, fetchUserNotifications } from '@finect/front-core/User';
import { ScrollAnchor } from '@finect/front-core/ScrollAnchor';
import { getLiveEvents } from './store/liveEvents/actions';
import { routes } from './scenes/routes';
import { setPlusModal } from './store/settings/actions';
import { LoginModal, PlusLimitModal } from './components';
import { AppContextual } from './components/AppContextual';
import { thenFunction } from './utils/imported';
import { preloadPromoSetting } from './scenes/Plus/PlusPromo/preload';

const PAYMENT_SUBSCRIPTION_URL = '/servicios/finect-subscription';
const PAYMENT_PLUS_URL = '/servicios/finect-plus';

/* eslint-disable */
const GlobalStyles = createGlobalStyle`
  ${reset};
`;
/* eslint-enable */

/**
 * La ruta de una location, sin query ni ancla.
 *
 * En SSR la App recibe `location={{ pathname: req.url }}`, y `req.url` incluye la query: sin quitarla,
 * un enlace con `?utm_source=` no casa con su propia ruta y los guards deciden sobre otra cosa.
 *
 * @param location Location de react-router, o la que fabrica el SSR
 */
export const getRoute = (location: ?Object): string => (location?.pathname || '').split(/[?#]/)[0];

/**
 * Si una ruta es la página de compra de Plus de la que hay que sacar a un suscriptor.
 *
 * Las condiciones se exceptúan: un suscriptor sí puede leerlas. Se compara con matchPath, que no
 * distingue caja y —al contrario que startsWith— no confundiría un futuro /finect-plus-otro.
 *
 * @param route Ruta, ya sin query
 */
export const isPlusPurchasePath = (route: string): boolean =>
  Boolean(matchPath(route, { path: PAYMENT_PLUS_URL })) &&
  !matchPath(route, { path: `${PAYMENT_PLUS_URL}/condiciones`, exact: true });

/**
 * Si la petición es la vuelta de Stripe después de pagar.
 *
 * Stripe devuelve al comprador a la propia página de compra con `?success=1&session=cs_…`, y es ahí
 * donde el checkout confirma la suscripción, emite el `purchase` de GA4, cierra la sesión de la app
 * nativa y pinta la pantalla de «¡Felicidades!». Todo eso vive en el componentDidMount de la escena,
 * así que si el guard de abajo se lleva por delante esta URL, no se pinta nada de eso.
 *
 * Y se la llevaba: al volver de pagar el usuario YA es Plus —el webhook de Stripe escribe el flag,
 * y compite con el navegador—, así que cuando el webhook llegaba primero la escena no montaba y la
 * compra se perdía para la analítica (la suscripción no: esa la había guardado el webhook).
 *
 * @param location Location de react-router, o la que fabrica el SSR
 */
export const isCheckoutReturn = (location: ?Object): boolean =>
  // Con valor: la vuelta real llega con `success=1`, y un `?success=` vacío escrito a mano no puede
  // abrir la página de compra a un suscriptor.
  /[?&]success=[^&#]/.test(location?.search || '');

const GoogleSignIn = React.lazy(() => import('@finect/front-core/GoogleSignIn').catch(() => thenFunction()));

export function fetchAppInitialData(cookie: string = 'include') {
  return function (dispatch: Dispatch, getState: Function) {
    const promises = [];
    if (!getState().user.user.hasOwnProperty('isLoggedIn')) promises.push(dispatch(getLoggedUser(cookie)));
    if (!getState().liveEvents.fetched) promises.push(dispatch(getLiveEvents()));

    return Promise.all(promises);
  };
}

const mapStateToProps = state => ({
  user: state.user.user,
  openPlusModal: state.settings.openPlusModal
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchUserNotifications,
      fetchAppInitialData,
      setPlusModal
    },
    dispatch
  )
});

type Props = {
  user: Object,
  openPlusModal: boolean | number,
  actions: Object,
  children: any,
  omniNavData?: Object,
  location?: Object,
  /** Contexto del StaticRouter, solo en SSR: por ahí se responde un 30x */
  staticContext?: Object
};

type State = {
  showPlus: boolean,
  mounted: boolean
};

class App extends React.Component<Props, State> {
  state = {
    showPlus: false,
    // GoogleSignIn es client-only (React.lazy). renderToString no soporta Suspense que se
    // suspende, así que en SSR emitiría el error #419 y al hidratar el #421. Lo montamos solo
    // tras la hidratación para evitar ambos.
    mounted: false
  };

  componentDidMount() {
    this.props.actions.fetchAppInitialData();
    const showPlus = new URLSearchParams(window.location.search).get('showPlus') === '1';
    this.setState({ showPlus, mounted: true });
  }

  // El SSR pasa la query en `params` y la ruta en `location`: con las dos se sabe si la petición es
  // de campaña.
  static loadInitialData(store, cookie, match, params, location) {
    return Promise.all([store.dispatch(fetchAppInitialData(cookie)), preloadPromoSetting(store, params, location)]);
  }

  newsletterAction = (preferences: Object) => {
    this.props.actions.fetchUserNotifications(preferences);
  };

  handlePlusModalToggle = debounce(() => this.props.actions.setPlusModal(false), 1000);

  render() {
    if (!this.props.user.hasOwnProperty('isLoggedIn')) return null;
    const { user, children, omniNavData, location, staticContext } = this.props;
    const pathname = getRoute(location);

    const omninavShadowMatches = routes
      .filter(({ omninavShadow = false }) => omninavShadow)
      .filter(route => matchPath(pathname, route));

    const showCookiesCMP = pathname && !pathname.startsWith(PAYMENT_SUBSCRIPTION_URL);

    const isPlus = user?.plus;

    // Un suscriptor no tiene nada que hacer en la página de compra. Esta regla sigue aquí y no en la
    // ruta (como sí hace <PlusRoute> con las páginas de /plus) porque esa ruta sirve a la vez
    // finect-plus y finect-subscription, así que la condición no es «esta ruta» sino «esta ruta con
    // este producto», y eso se lee de la URL.
    //
    // La vuelta de Stripe se exceptúa: ahí el comprador ya es Plus y aun así tiene que pintarse la
    // escena, que es la que confirma la compra y la cuenta (ver isCheckoutReturn).
    if (isPlus && isPlusPurchasePath(pathname) && !isCheckoutReturn(location)) {
      // En servidor se responde una redirección; devolver null aquí dejaba un 200 con el cuerpo
      // vacío para un suscriptor —o para un crawler con su cookie— que entrara en la página de compra.
      if (staticContext) {
        staticContext.statusCode = 302;
        staticContext.redirectURL = '/plus/seleccion';
      } else if (typeof window !== 'undefined') {
        requestAnimationFrame(() => window.location.assign('/plus/seleccion'));
      }

      return null;
    }

    return (
      <ThemeProvider theme={theme}>
        {showCookiesCMP && (
          <CookiesCMP user={user} cmpDomain={isPreRelease ? 'consent.finect.com' : 'cdn.privacy-mgmt.com'} />
        )}

        {this.state.mounted && (
          <Suspense fallback={null}>
            <GoogleSignIn />
          </Suspense>
        )}

        <ScrollAnchor location={this.props.location} />
        <AppContextual />
        <Main>
          <GlobalStyles />

          <OmniNav
            user={this.props.user}
            finectApp="front-products"
            omniNavData={omniNavData}
            withShadow={Boolean(omninavShadowMatches.length)}
            showPlus={this.state.showPlus || this.props.openPlusModal}
            onClosePlus={this.handlePlusModalToggle}
          />

          <LoginModal />
          <PlusLimitModal url={`${PAYMENT_PLUS_URL}/comprar`} />

          <Content fullHeight>{children}</Content>
          <Footer user={user} withConversations newsletterAction={this.newsletterAction} />
        </Main>
      </ThemeProvider>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
export const AppWithRouter = withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
